import { useEffect, useState } from "react"
import { Card, Button, Form, Select, Typography, Flex, Tag, Spin } from "antd"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { getGuideDigitalWaivers } from "store/digital-waiver"
import { getGuideTrip } from "store/trips"
import { CircledClose } from "ui/Icon/DSIcon/CircledClose"
import { DocumentsAddedModal } from "pagesV2/pro/DigitalWaiverAndContracts/DocumentsAdded/modal/DocumentsAdded"
import { useWizardContext } from "../WizardContext"
import styles from "pagesV2/pro/Experiences/ListingDigitalWaiver/index.module.css"

const { Title, Text } = Typography

/**
 * Digital Waiver step — select and assign waivers to the experience.
 *
 * Uses raw FormData (not serialize()) to match the existing API contract.
 * selectedWaivers is tracked in local state; the registered save handler
 * builds the payload with _destroy for removed items.
 */
export const WaiverStep = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { id } = useParams()
  const [form] = Form.useForm()
  const { stepSaveHandlersRef } = useWizardContext()

  const [digitalWaivers, setDigitalWaivers] = useState([])
  const [selectedWaivers, setSelectedWaivers] = useState([])
  const [errorMessage, setErrorMessage] = useState("")
  const [isModalOpen, setIsModalOpen] = useState(false)

  const { items, loading } = useSelector((state) => state.digitalWaiver)
  const dropdownOptions = items.map((item) => ({ label: item.name, value: item.id }))

  useEffect(() => {
    dispatch(getGuideDigitalWaivers(undefined, undefined, true))
    dispatch(getGuideTrip(id)).then((res) => {
      setDigitalWaivers(res.data.digital_waiver_experiences || [])
    })
  }, [dispatch, id])

  useEffect(() => {
    if (digitalWaivers.length > 0 && items.length > 0) {
      const ids = digitalWaivers.map((dw) => dw.digital_waiver_id)
      setSelectedWaivers(ids)
      form.setFieldsValue({ waivers: ids })
    }
  }, [digitalWaivers, items, form])

  const buildWaiverFormData = (selectedWaiverIds) => {
    const formData = new FormData()
    const existingWaiversMap = {}
    digitalWaivers.forEach((w) => {
      existingWaiversMap[w.digital_waiver_id] = w.id
    })

    let formIndex = 0
    selectedWaiverIds.forEach((digitalWaiverId) => {
      if (existingWaiversMap[digitalWaiverId]) {
        formData.append(
          `trip[digital_waiver_experiences_attributes][${formIndex}][id]`,
          existingWaiversMap[digitalWaiverId]
        )
      } else {
        formData.append(
          `trip[digital_waiver_experiences_attributes][${formIndex}][digital_waiver_id]`,
          digitalWaiverId
        )
      }
      formIndex++
    })

    digitalWaivers.forEach((waiver) => {
      if (!selectedWaiverIds.includes(waiver.digital_waiver_id)) {
        formData.append(`trip[digital_waiver_experiences_attributes][${formIndex}][id]`, waiver.id)
        formData.append(`trip[digital_waiver_experiences_attributes][${formIndex}][_destroy]`, "true")
        formIndex++
      }
    })

    formData.append("trip[step]", "digital_waivers")
    return formData
  }

  // Register save handler
  useEffect(() => {
    stepSaveHandlersRef.current["digital_waiver"] = async () => {
      const values = form.getFieldsValue()
      const selectedWaiverIds = values.waivers || []

      if (selectedWaiverIds.length > 5) {
        setErrorMessage("You can select up to 5 waivers only.")
        throw new Error("Too many waivers selected")
      }
      setErrorMessage("")

      return buildWaiverFormData(selectedWaiverIds)
    }
    return () => {
      delete stepSaveHandlersRef.current["digital_waiver"]
    }
  }, [stepSaveHandlersRef, form, digitalWaivers]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Spin spinning={loading}>
      <Card>
        <Flex justify="space-between">
          <Title level={3} style={{ marginBottom: 24 }}>
            {t("listings.tabs.digital_waiver.digital_waiver_information")}
          </Title>
          <Button color="primary" variant="solid" onClick={() => setIsModalOpen(true)}>
            {t("listings.tabs.digital_waiver.add_digital_waiver")}
          </Button>
        </Flex>

        <Form form={form} layout="vertical">
          <Form.Item name="waivers">
            <Flex>
              <Text>{t("listings.tabs.digital_waiver.add_digital_waiver_document")}</Text>
            </Flex>
            <Select
              mode="multiple"
              style={{ width: "100%" }}
              placeholder="Select waiver documents"
              optionLabelProp="label"
              options={dropdownOptions}
              value={selectedWaivers}
              tagRender={({ label, value, closable }) => {
                const handleClose = (e) => {
                  setErrorMessage("")
                  e.preventDefault()
                  const current = form.getFieldValue("waivers") || []
                  const next = current.filter((item) => item !== value)
                  form.setFieldsValue({ waivers: next })
                  setSelectedWaivers(next)
                }
                return (
                  <Tag
                    className={styles.categoryChip}
                    key={value}
                    closable={closable}
                    onClose={handleClose}
                    closeIcon={<CircledClose width={16} height={16} fill="var(--color-primary)" />}
                  >
                    {label}
                  </Tag>
                )
              }}
              onChange={(waivers) => {
                form.setFieldsValue({ waivers })
                setSelectedWaivers(waivers)
              }}
            />
          </Form.Item>
          {errorMessage && <Text type="danger">{errorMessage}</Text>}
        </Form>
      </Card>

      {isModalOpen && (
        <DocumentsAddedModal
          isLoading={false}
          visible={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          onSave={() => {
            setIsModalOpen(false)
            dispatch(getGuideDigitalWaivers(undefined, undefined, true))
          }}
        />
      )}
    </Spin>
  )
}
